import React, { FC, SyntheticEvent, useEffect, useMemo } from "react";

import { SerializedStyles } from "@emotion/react";
import { Sidebar, ToggleSwitch } from "@epignosis_llc/gnosis";
import { useResponsive } from "ahooks";

import { mainMenuInner } from "@components/MainNav/MainMenu.styles";

import { featureFlags } from "@config";

import { getIsSandboxAvailable } from "@api/sandbox";
import { getMenuEntries } from "@components/MainNav/helpers";
import { useApplyTranslations, useCms } from "@hooks";
import { useToggleSandboxModeURL } from "@hooks/api/user";
import usePollingQuery from "@hooks/usePollingQuery";
import authService from "@lib/services/AuthService";
import { useConfigurationStore } from "@stores";
import useUIStore from "@stores/ui";
import { isMainMenuItemActiveForDrawer } from "@utils/helpers";
import permissions from "@utils/permissions";

import queryKeys from "@constants/queryKeys";
import userRoles from "@constants/userRoles";
import { SimpleLearnerDashboard } from "@views/Dashboard/constants";

import HelpMenuItem from "@components/MainNav/components/HelpMenuItem";
import MenuWithSubmenus from "@components/MainNav/components/MenuWithSubmenus";
import { MainNavData } from "@components/MainNav/MainNav";
import { useOnboardingChecklist } from "@components/ProductFruits/hooks/useOnBoardingChecklist";

import { MainDrawerContent, MainDrawerContentEnum } from "types/common";

type MainMenuProps = {
  isCollapsed?: boolean;
  onToggle?: () => void;
  topMenuOnClick?: (e: SyntheticEvent) => void;
  bottomMenuOnClick?: (e: SyntheticEvent, string: MainDrawerContent) => void;
  navState?: MainNavData;
};

const MainMenu: FC<MainMenuProps> = ({
  isCollapsed = false,
  navState = null,
  bottomMenuOnClick = (): void => undefined,
}) => {
  const { t } = useApplyTranslations();
  const { md } = useResponsive();
  const { customPages } = useCms({ isPublic: false });
  const { hideMainDrawer } = useUIStore((state) => state);
  const { userProfileData, domainSettings, unreadNotifications } = useConfigurationStore();
  const canEnableReverseTrial = userProfileData?.policies?.can_enable_reverse_trial ?? false;
  const {
    cms,
    is_in_trial: isTrialEnabled = false,
    features_allowed_in_plan: featuresAllowedInPlan,
    is_sandbox: isSandbox = false,
    is_sandbox_available: isSandboxAvailable = false,
    supports_sandbox_switching: supportsSandboxSwitching = false,
  } = domainSettings ?? {};
  const menuEntries = getMenuEntries({
    cms: customPages,
    isTrialEnabled,
    canEnableReverseTrial,
    featuresAllowedInPlan,
  });

  const userRole = authService.getDefaultRole();
  const isLearner = userRole === userRoles.LEARNER;
  const isInstructor = userRole === userRoles.INSTRUCTOR;
  const isAdmin = userRole === userRoles.ADMINISTRATOR;
  const isImpersonationEnabled = userProfileData?.impersonated;
  const isPortalOwner = userProfileData?.is_owner;
  const isCustomHomePageEnabled = cms && cms?.published;
  const hasCustomPages = customPages && customPages?.length > 0;
  const hasSimpleDashboard =
    domainSettings?.simple_learner_dashboard === SimpleLearnerDashboard.ACTIVE;

  // Permissions related
  const { canAccessCourses, canAccessUnionCourses, canUpdateCourseUnits } =
    permissions.coursesPermissions;
  const { canAccessCatalog } = permissions.catalogPermissions;
  const { canAccessCalendar } = permissions.calendarPermissions;
  const { canReadBranches } = permissions.branchPermissions;
  const { canExport, canImport } = permissions.importExportPermissions;
  const { canAccessUsers } = permissions.usersPermissions;
  const { canReadGroups } = permissions.groupPermissions;
  const { canAccessTimeline, canAccessReports, canAccessCourseReports } =
    permissions.reportsPermissions;
  const { canAccessAccount, canAccessStoreWithSubscription, canAccessSubscription } =
    permissions.accountPermissions;
  const { canAccessAutomations } = permissions.automationsPermissions;
  const { canAccessNotifications } = permissions.notificationsPermissions;
  const { canAccessCategories } = permissions.categoriesPermissions;
  const { canAccessUserTypes } = permissions.userTypesPermissions;
  const { canAccessHelp } = permissions.helpCenterPermissions;
  const { canAccessConferences } = permissions.conferencesPermissions;
  const { canViewSkills, canAccessSkills, canAccessSkillSettings } = permissions.skillsPermissions;

  const allowCatalogView = canAccessCatalog();

  const allowMyCoursesView = isLearner && canAccessCourses() && !hasSimpleDashboard;
  const allowAccountAndSettingsView =
    !isLearner &&
    (canAccessAccount() ||
      canExport() ||
      canImport() ||
      canAccessCategories() ||
      canAccessUserTypes());
  const allowCoursesView = !isLearner && (canAccessCourses() || canAccessUnionCourses());
  const allowCalendarView = canAccessCalendar();
  const allowBranchesView = canReadBranches();
  const allowUsersView = canAccessUsers();
  const allowGroupsView = canReadGroups();
  const allowUpdateCourseUnits = canUpdateCourseUnits();
  const allowGradingHubView = isInstructor && allowUpdateCourseUnits;

  const allowReportsView =
    ((isAdmin ? canAccessReports() : canAccessCourseReports()) || canAccessTimeline()) &&
    !isLearner;
  const allowAutomationsView = canAccessAutomations() && isAdmin;
  const allowNotificationsView = canAccessNotifications() && isAdmin;
  const allowCourseStoreView = canAccessStoreWithSubscription() && canAccessCourses() && isAdmin;
  const allowSubscriptionView = isAdmin && canAccessSubscription();
  const allowHelpCenterView = canAccessHelp() && !isSandbox;
  const allowConferencesView = canAccessConferences() && isInstructor;
  const allowViewSandbox = supportsSandboxSwitching && isPortalOwner;
  const allowSkillsView = featureFlags.skills && canViewSkills() && isAdmin;
  const allowSkillsSettingsView = featureFlags.skills && canAccessSkillSettings() && isAdmin;
  const allowMySkillsView = featureFlags.skills && canAccessSkills() && !isAdmin;

  const handleBottomMenuClick = (
    e: React.MouseEvent<HTMLLIElement | HTMLAnchorElement>,
    drawerKey: string,
  ): void => {
    // Redirect in dashboard and open onboarding tour

    bottomMenuOnClick(e, drawerKey as MainDrawerContent);
  };
  const { pending_assignments = 0, pending_ilts = 0 } = unreadNotifications?.grading_hub ?? {};
  const totalPendingItems = pending_assignments + pending_ilts;

  // Filter primary menu items based on the permissions
  const primaryMenuEntries = useMemo(() => {
    return menuEntries[0].filter((menuEntry) => {
      if (menuEntry.drawerKey === "role") return true;
      if (menuEntry.drawerKey === "catalog") return allowCatalogView;
      if (menuEntry.drawerKey === "myCourses") return allowMyCoursesView;
      if (menuEntry.drawerKey === "accountAndSettings") return allowAccountAndSettingsView;
      if (menuEntry.drawerKey === "courses") return allowCoursesView;
      if (menuEntry.drawerKey === "calendar") return allowCalendarView;
      if (menuEntry.drawerKey === "branches") return allowBranchesView;
      if (menuEntry.drawerKey === "groups") return allowGroupsView;
      if (menuEntry.drawerKey === "gradingHub") return allowGradingHubView;
      if (menuEntry.drawerKey === "users") return allowUsersView;
      if (menuEntry.drawerKey === "reports") return allowReportsView;
      if (menuEntry.drawerKey === "courseStore") return allowCourseStoreView;
      if (menuEntry.drawerKey === "automations") return allowAutomationsView;
      if (menuEntry.drawerKey === "notifications") return allowNotificationsView;
      if (menuEntry.drawerKey === "billingAndSubscriptionSettings") return allowSubscriptionView;
      if (menuEntry.drawerKey === "customPages") return isCustomHomePageEnabled && hasCustomPages;
      if (menuEntry.drawerKey === "conferences") return allowConferencesView;
      if (menuEntry.drawerKey === "home") return true;
      if (menuEntry.drawerKey === "profile") return true;
      if (menuEntry.drawerKey === "dashboard") return true;
      if (menuEntry.drawerKey === "skills") return allowSkillsView;
      if (menuEntry.drawerKey === "skillsSettings") return allowSkillsSettingsView;
      if (menuEntry.drawerKey === "mySkills") return allowMySkillsView;
      return false;
    });
  }, [
    menuEntries,
    allowCatalogView,
    allowMyCoursesView,
    allowAccountAndSettingsView,
    allowCoursesView,
    allowCalendarView,
    allowBranchesView,
    allowGroupsView,
    allowGradingHubView,
    allowUsersView,
    allowReportsView,
    allowCourseStoreView,
    allowAutomationsView,
    allowNotificationsView,
    allowSkillsView,
    allowSkillsSettingsView,
    allowMySkillsView,
    isCustomHomePageEnabled,
    hasCustomPages,
    allowSubscriptionView,
    allowConferencesView,
  ]);

  // Filter secondary menu items based on the permissions
  const secondaryMenuEntries = useMemo(() => {
    return menuEntries[1].filter((menuEntry) => {
      if (menuEntry.drawerKey === MainDrawerContentEnum.HELP_CENTER) return allowHelpCenterView; // Help center feature
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuEntries, isImpersonationEnabled, allowHelpCenterView]);

  const getBadgeNumber = (drawerKey: string): number => {
    if (drawerKey === "calendar") return unreadNotifications?.calendar?.upcoming ?? 0;
    if (drawerKey === "gradingHub") return totalPendingItems ?? 0;
    return 0;
  };

  const handleTopMenuClick = (): void => {
    hideMainDrawer();
  };

  const { checklistData } = useOnboardingChecklist();

  //Sandbox related
  const { mutate: switchBetweenSandboxAndMainDomain } = useToggleSandboxModeURL();

  const onSandboxToggleClick = (): void => {
    setTimeout(() => {
      switchBetweenSandboxAndMainDomain();
    }, 500);
  };

  const { shouldPollSandbox, setShouldPollSandbox } = useConfigurationStore();

  // Polling logic for isSandboxAvailable
  const {
    stopPolling,
    startPolling,
    data: polledData,
  } = usePollingQuery({
    queryKey: [queryKeys.sandbox.isSandboxAvailable],
    queryFn: getIsSandboxAvailable,
    interval: 5000,
    staleTime: 0,
    cacheTime: 0,
    maxTries: 6,
    onSuccess: (data) => {
      if (data._data.is_sandbox_available) {
        stopPolling();
        return;
      }
    },
  });

  const polledIsSandboxAvailable = polledData?._data?.is_sandbox_available;

  useEffect(() => {
    if (shouldPollSandbox) {
      startPolling();
      setShouldPollSandbox(false);
    }
  }, [setShouldPollSandbox, shouldPollSandbox, startPolling]);

  const isSandboxToggleSwitchEnabled = isSandboxAvailable || polledIsSandboxAvailable;

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      className="sidebar-container"
      aria-label={t("a11y.general.navigationBar")}
      role="navigation"
    >
      <div
        className="inner-container"
        css={(theme): SerializedStyles => mainMenuInner(theme, { isCollapsed })}
      >
        <ul id="main-menu" className="main-menu" role="list">
          {primaryMenuEntries.map(({ url, title, icon, iconAfter, drawerKey, submenu }) => {
            const withBadge = ["calendar", "gradingHub"].some((item) => item === drawerKey);

            return (
              <MenuWithSubmenus
                key={title}
                title={title}
                url={url}
                icon={icon}
                iconAfter={iconAfter}
                drawerKey={drawerKey}
                withBadge={withBadge}
                badgeNumber={getBadgeNumber(drawerKey ?? "")}
                isCollapsed={isCollapsed}
                submenu={submenu}
                handleClick={handleTopMenuClick}
                productFruitsChecklist={checklistData}
              />
            );
          })}
        </ul>

        <div>
          {md && allowViewSandbox && (
            <ul className="sandbox-toggle-item">
              <ToggleSwitch
                id="sandbox-toggle"
                labelledById="sandbox-toggle"
                labelAfter={!isCollapsed ? t("sandbox.sandboxToggleItem") : ""}
                tooltip={t("sandbox.sandboxToggleItemTooltip")}
                defaultChecked={isSandbox}
                isDisabled={!isSandboxToggleSwitchEnabled}
                variant="primary"
                showOutlineBorder={true}
                onChange={(): void => onSandboxToggleClick()}
              />
            </ul>
          )}

          <ul className="secondary-menu">
            {secondaryMenuEntries.map(({ title, icon, drawerKey }) => {
              return (
                <HelpMenuItem
                  key={title}
                  title={title}
                  drawerKey={drawerKey ?? ""}
                  icon={icon}
                  isActive={isMainMenuItemActiveForDrawer(navState, drawerKey as MainDrawerContent)}
                  isCollapsed={isCollapsed}
                  handleClick={(e, drawerKey): void =>
                    handleBottomMenuClick(e, drawerKey as MainDrawerContent)
                  }
                />
              );
            })}
          </ul>
        </div>
      </div>
    </Sidebar>
  );
};

export default MainMenu;
